/**
 * Date Range Utilities
 * Helper functions for date range filtering and calculations
 */

/**
 * Convert a Date to ISO date string (YYYY-MM-DD)
 */
export function toISODate(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if an item's date range overlaps with the reporting period range
 * Inclusive on both ends
 * @param {Date|string} itemStart - Start date of the item
 * @param {Date|string} itemEnd - End date of the item
 * @param {Date|string} rangeStart - Start date of the reporting period
 * @param {Date|string} rangeEnd - End date of the reporting period
 * @returns {boolean} True if there's any overlap
 */
export function overlapsRange(itemStart, itemEnd, rangeStart, rangeEnd) {
  if (!itemStart || !itemEnd || !rangeStart || !rangeEnd) return false;
  
  const itemStartDate = itemStart instanceof Date ? itemStart : new Date(itemStart);
  const itemEndDate = itemEnd instanceof Date ? itemEnd : new Date(itemEnd);
  const rangeStartDate = rangeStart instanceof Date ? rangeStart : new Date(rangeStart);
  const rangeEndDate = rangeEnd instanceof Date ? rangeEnd : new Date(rangeEnd);
  
  if (isNaN(itemStartDate.getTime()) || isNaN(itemEndDate.getTime()) || 
      isNaN(rangeStartDate.getTime()) || isNaN(rangeEndDate.getTime())) {
    return false;
  }
  
  // Normalize to start of day for comparison
  const itemStartNormalized = new Date(itemStartDate.getFullYear(), itemStartDate.getMonth(), itemStartDate.getDate());
  const itemEndNormalized = new Date(itemEndDate.getFullYear(), itemEndDate.getMonth(), itemEndDate.getDate());
  const rangeStartNormalized = new Date(rangeStartDate.getFullYear(), rangeStartDate.getMonth(), rangeStartDate.getDate());
  const rangeEndNormalized = new Date(rangeEndDate.getFullYear(), rangeEndDate.getMonth(), rangeEndDate.getDate());
  
  // Overlap exists if: itemStart <= rangeEnd AND itemEnd >= rangeStart
  return itemStartNormalized <= rangeEndNormalized && itemEndNormalized >= rangeStartNormalized;
}

/**
 * Check if a single date falls within the reporting period range
 * @param {Date|string} date - The date to check
 * @param {Date|string} rangeStart - Start date of the reporting period
 * @param {Date|string} rangeEnd - End date of the reporting period
 * @returns {boolean} True if date is within range
 */
export function isInRange(date, rangeStart, rangeEnd) {
  if (!date || !rangeStart || !rangeEnd) return false;
  
  const dateObj = date instanceof Date ? date : new Date(date);
  const rangeStartDate = rangeStart instanceof Date ? rangeStart : new Date(rangeStart);
  const rangeEndDate = rangeEnd instanceof Date ? rangeEnd : new Date(rangeEnd);
  
  if (isNaN(dateObj.getTime()) || isNaN(rangeStartDate.getTime()) || isNaN(rangeEndDate.getTime())) {
    return false;
  }
  
  // Normalize to start of day
  const dateNormalized = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
  const rangeStartNormalized = new Date(rangeStartDate.getFullYear(), rangeStartDate.getMonth(), rangeStartDate.getDate());
  const rangeEndNormalized = new Date(rangeEndDate.getFullYear(), rangeEndDate.getMonth(), rangeEndDate.getDate());
  
  return dateNormalized >= rangeStartNormalized && dateNormalized <= rangeEndNormalized;
}

/**
 * Format a date range for display
 * @param {Date|string} start - Start date
 * @param {Date|string} end - End date (end-exclusive, will be displayed as inclusive by subtracting 1 day)
 * @returns {string} Formatted range string (e.g., "Oct 1–Dec 31, 2025")
 */
export function formatDateRange(start, end) {
  if (!start || !end) return '';
  
  const startDate = start instanceof Date ? start : new Date(start);
  let endDate = end instanceof Date ? end : new Date(end);
  
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return '';
  
  // For display, convert end-exclusive to inclusive by subtracting 1 day
  // This makes the range human-friendly (e.g., "Jan 1–Jan 31, 2026" instead of "Jan 1–Feb 1, 2026")
  const displayEndDate = new Date(endDate);
  displayEndDate.setDate(displayEndDate.getDate() - 1);
  
  const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const endStr = displayEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  
  // If same year, only show year once
  if (startDate.getFullYear() === displayEndDate.getFullYear()) {
    const startStrShort = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${startStrShort}–${endStr}`;
  }
  
  return `${startStr}–${endStr}`;
}

